import { html, nothing } from "lit";
import type {
  SessionArtifactFileSummary,
  SessionArtifactSummaryTotals,
} from "./chat-file-changes-summary.ts";

export type ChatComposerFileReviewProps = {
  files: SessionArtifactFileSummary[];
  totals: SessionArtifactSummaryTotals;
  busy?: boolean;
  /** When true, only the header row is shown (Cursor-style collapsed file list). */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onFileClick?: (changeId: string, path: string, artifactSessionKey?: string) => void;
  onReview?: () => void;
  onKeepAll?: () => void;
  onUndoAll?: () => void;
  error?: string | null;
};

function opLabel(op: SessionArtifactFileSummary["op"]): string {
  if (op === "manual") {
    return "modified";
  }
  return op;
}

export function renderChatComposerFileReview(props: ChatComposerFileReviewProps) {
  if (props.files.length === 0) {
    return nothing;
  }

  const fileCount = props.totals.files || props.files.length;
  const title = `${fileCount} file${fileCount === 1 ? "" : "s"}`;
  const collapsed = props.collapsed === true;

  const stop = (event: Event) => {
    event.stopPropagation();
  };

  return html`
    <div
      class="chat-composer-file-review ${collapsed ? "chat-composer-file-review--collapsed" : ""}"
      role="region"
      aria-label="Files changed this run"
    >
      <div class="chat-composer-file-review__header">
        <button
          type="button"
          class="chat-composer-file-review__toggle"
          aria-expanded=${!collapsed}
          aria-label=${collapsed ? "Expand file list" : "Collapse file list"}
          ?disabled=${!props.onToggleCollapsed}
          @click=${() => props.onToggleCollapsed?.()}
        >
          <span class="chat-composer-file-review__chevron" aria-hidden="true">▸</span>
          <span class="chat-composer-file-review__title-row">
            <span class="chat-composer-file-review__title">${title}</span>
            <span class="chat-composer-file-review__totals muted">
              <span class="chat-file-changes__add">+${props.totals.additions}</span>
              <span class="chat-file-changes__del">−${props.totals.deletions}</span>
            </span>
          </span>
        </button>
        <div class="chat-composer-file-review__actions">
          ${
            props.onReview
              ? html`
                <button
                  type="button"
                  class="chat-composer-file-review__btn chat-composer-file-review__btn--review btn btn--sm"
                  ?disabled=${props.busy}
                  @click=${(e: Event) => {
                    stop(e);
                    props.onReview?.();
                  }}
                >
                  Review
                </button>
              `
              : nothing
          }
          ${
            props.onKeepAll
              ? html`
                <button
                  type="button"
                  class="chat-composer-file-review__btn chat-composer-file-review__btn--keep btn btn--sm"
                  ?disabled=${props.busy}
                  @click=${(e: Event) => {
                    stop(e);
                    props.onKeepAll?.();
                  }}
                >
                  Keep all
                </button>
              `
              : nothing
          }
          ${
            props.onUndoAll
              ? html`
                <button
                  type="button"
                  class="chat-composer-file-review__btn chat-composer-file-review__btn--undo btn btn--sm"
                  ?disabled=${props.busy}
                  @click=${(e: Event) => {
                    stop(e);
                    props.onUndoAll?.();
                  }}
                >
                  Undo all
                </button>
              `
              : nothing
          }
        </div>
      </div>
      ${
        props.error
          ? html`<div class="chat-composer-file-review__error callout danger">${props.error}</div>`
          : nothing
      }
      ${
        collapsed
          ? nothing
          : html`
      <ul class="chat-composer-file-review__files">
        ${props.files.map(
          (f) => html`
            <li>
            <button
              type="button"
              class="chat-composer-file-review__file"
              ?disabled=${!f.changeId || !props.onFileClick}
              @click=${() => {
                if (f.changeId && props.onFileClick) {
                  props.onFileClick(f.changeId, f.path, f.sessionKey);
                }
              }}
            >
              <span class="chat-composer-file-review__op chat-composer-file-review__op--${f.op}"
                >${opLabel(f.op)}</span
              >
              <span class="chat-composer-file-review__path mono" title=${f.path}>${f.path}</span>
              <span class="chat-composer-file-review__stats muted">
                +${f.additions} −${f.deletions}
              </span>
            </button>
            </li>
          `,
        )}
      </ul>
      `
      }
    </div>
  `;
}
